import { Avatar, Box, Drawer, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import { useLocation, useNavigate } from 'react-router';
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { AccountBalanceWalletOutlined, AnalyticsOutlined, ApartmentOutlined, ImageOutlined, StarOutlineRounded } from '@mui/icons-material';
import { propertiesNavItem, visibleNavItems } from '../../pages/propertyCatalog';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../shared/service/dataprovider';
import tokens from '../../shared/theme/tokens';
import lockup from '../../assets/svg/tenantport-lockup.svg';
import mark from '../../assets/svg/tenantport-mark.svg';
import LogoutConfirm from './LogoutConfirm';

const NAV_ITEMS = [
  {
    key: 'leases',
    label: 'Leases',
    icon: PersonOutlineOutlinedIcon,
    path: '/dashboard',
    isActive: pathname => pathname.startsWith('/dashboard') || pathname.startsWith('/lease'),
  },
  {
    key: 'banners',
    label: 'Banners',
    icon: ImageOutlined,
    path: '/banners',
    isActive: pathname => pathname.startsWith('/banners'),
    superAdminOnly: true,
  },
  {
    ...propertiesNavItem,
    icon: ApartmentOutlined,
    isActive: pathname => pathname.startsWith('/properties'),
  },
  {
    key: 'reports',
    label: 'Applicants',
    icon: AnalyticsOutlined,
    path: '/reports',
    isActive: pathname => pathname === '/reports',
  },
  {
    key: 'depositAssessment',
    label: 'Deposit assessment',
    icon: AccountBalanceWalletOutlined,
    path: '/reports/deposit-assessment',
    isActive: pathname => pathname.startsWith('/reports/deposit-assessment'),
    superAdminOnly: true,
  },
  {
    key: 'moveInSurveys',
    label: 'Move-In Surveys',
    icon: StarOutlineRounded,
    path: '/move-in-surveys',
    isActive: pathname => pathname.startsWith('/move-in-surveys'),
  },
];

function NavItem({ item, mini, active, onClick }) {
  const Icon = item.icon;
  const body = (
    <Stack
      direction="row"
      alignItems="center"
      onClick={onClick}
      gap={1.25}
      sx={{
        cursor: 'pointer',
        borderRadius: `${tokens.radius.sm}px`,
        px: mini ? 1 : 1.25,
        py: 1,
        justifyContent: mini ? 'center' : 'flex-start',
        color: active ? tokens.colors.primaryDeep : tokens.colors.inkSecondary,
        backgroundColor: active ? tokens.colors.primarySoft : 'transparent',
        '&:hover': {
          backgroundColor: active ? tokens.colors.primarySoft : tokens.colors.hover,
          color: active ? tokens.colors.primaryDeep : tokens.colors.ink,
        },
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
      {!mini && (
        <Typography variant="body2" sx={{ fontWeight: active ? 600 : 500, color: 'inherit' }}>
          {item.label}
        </Typography>
      )}
    </Stack>
  );
  return mini ? (
    <Tooltip title={item.label} placement="right">
      {body}
    </Tooltip>
  ) : (
    body
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth0();
  const { t } = useTranslation();
  const theme = useTheme();
  const mini = useMediaQuery(theme.breakpoints.down('md'));
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['user'],
    queryFn: () => api.get('/v1/admin/profile'),
  });
  const isSuperAdmin = profile?.data?.isSuperAdmin;
  const canManagePropertyCatalog = profile?.data?.canManagePropertyCatalog;
  const navItems = visibleNavItems(NAV_ITEMS, { isSuperAdmin, canManagePropertyCatalog });

  const width = mini ? tokens.layout.sidebarMiniWidth : tokens.layout.sidebarWidth;
  const initials = (user?.name || user?.email || '?')
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            boxSizing: 'border-box',
            backgroundColor: tokens.colors.surface,
            width: `${width}px`,
            borderRight: `1px solid ${tokens.colors.border}`,
            // Keep logout above the support launcher docked at the bottom-left.
            pb: '104px',
          },
        }}
      >
        <Stack sx={{ pt: 2.5, pb: 2, px: mini ? 1 : 1.5, height: '100%' }}>
          <Box sx={{ px: mini ? 0.5 : 1.25, pb: 3, display: 'flex', justifyContent: mini ? 'center' : 'flex-start' }}>
            <img src={mini ? mark : lockup} alt="TenantPort" style={{ height: mini ? 30 : 32, maxWidth: '100%' }} />
          </Box>

          <Stack spacing={0.5} sx={{ flex: 1 }}>
            {navItems.map(item => (
              <NavItem key={item.key} item={item} mini={mini} active={item.isActive(pathname)} onClick={() => navigate(item.path)} />
            ))}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            gap={1.25}
            sx={{
              borderTop: `1px solid ${tokens.colors.border}`,
              pt: 1.75,
              px: mini ? 0 : 0.5,
              justifyContent: mini ? 'center' : 'flex-start',
            }}
          >
            {!mini && (
              <>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: 12,
                    fontWeight: 600,
                    bgcolor: tokens.colors.primarySoft,
                    color: tokens.colors.primaryDeep,
                  }}
                >
                  {initials}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {user?.email}
                  </Typography>
                </Box>
              </>
            )}
            <Tooltip title={t('logout.title', 'Log out')} placement={mini ? 'right' : 'top'}>
              <Box
                onClick={() => setShowLogoutConfirm(true)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: tokens.colors.inkMuted,
                  borderRadius: `${tokens.radius.sm}px`,
                  p: 0.75,
                  '&:hover': { backgroundColor: tokens.colors.hover, color: tokens.colors.ink },
                }}
              >
                <ExitToAppRoundedIcon sx={{ fontSize: 20 }} />
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Drawer>
      {showLogoutConfirm && <LogoutConfirm handleClose={() => setShowLogoutConfirm(false)} />}
    </>
  );
}
